/**
 * #473: client-side topic grouping for the drawer’s Topics view.
 *
 * Conversations are grouped by their FIRST keyTopic (the primary tag) —
 * each conversation appears exactly once. Topic keys are normalized to
 * lowercase for grouping and displayed in Title Case. Sections are ordered
 * by the most recent conversation they contain; conversations without any
 * topic land in a trailing "Ungrouped" section.
 *
 * Pure by design so grouping is unit-testable without rendering the drawer.
 *
 * #2109 R3-B24: the "Ungrouped" label and the locale arrive from the copy
 * pack (the drawer passes them; the default reads `chatCopy()`). Title Case
 * is English orthography, so it applies only to an `en` locale — other
 * locales show the tag as stored.
 */

import type { ConversationSummary } from '../services/stevensV4Service';
import { chatCopy } from '../../config';

export interface TopicGroupingOptions {
  /** The trailing bucket’s label (`drawer.ungrouped`). */
  ungroupedLabel?: string;
  /** BCP 47 tag; Title Case only when it is English. */
  locale?: string;
}

export interface TopicGroup {
  /** Normalized (lowercase) topic key; '' for the ungrouped bucket. */
  key: string;
  /** Title Case display label ("delegation anxiety" → "Delegation Anxiety"). */
  label: string;
  conversations: ConversationSummary[];
}

function toTitleCase(topic: string): string {
  return topic.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}

/** Recency of a conversation for section ordering (lastActivity, else createdAt). */
function recencyOf(c: ConversationSummary): number {
  return c.lastActivity || c.createdAt || 0;
}

/** First non-blank topic tag, trimmed — or null when the conversation is untagged. */
function primaryTopicOf(c: ConversationSummary): string | null {
  // Defensive: a backend regression sending a non-array (e.g. a bare string)
  // must degrade to "untagged", not crash the Topics view.
  const list = Array.isArray(c.keyTopics) ? c.keyTopics : [];
  const primary = list.find((t) => typeof t === 'string' && t.trim().length > 0);
  return primary ? primary.trim() : null;
}

export function groupConversationsByTopic(
  conversations: ConversationSummary[],
  options: TopicGroupingOptions = {},
): TopicGroup[] {
  const ungroupedLabel = options.ungroupedLabel ?? chatCopy().drawer.ungrouped;
  const locale = options.locale ?? chatCopy().locale;
  const titleCase = /^en\b/i.test(locale);
  const groups = new Map<string, TopicGroup>();
  const ungrouped: ConversationSummary[] = [];

  for (const conversation of conversations) {
    const primary = primaryTopicOf(conversation);
    if (!primary) {
      ungrouped.push(conversation);
      continue;
    }
    const key = primary.toLowerCase();
    let group = groups.get(key);
    if (!group) {
      group = { key, label: titleCase ? toTitleCase(key) : key, conversations: [] };
      groups.set(key, group);
    }
    // Incoming order is preserved inside each group (the backend list is
    // already most-recent-first).
    group.conversations.push(conversation);
  }

  // Sections ordered by the most recent conversation within each group.
  const sections = [...groups.values()].sort((a, b) => {
    const maxA = Math.max(...a.conversations.map(recencyOf));
    const maxB = Math.max(...b.conversations.map(recencyOf));
    return maxB - maxA;
  });

  if (ungrouped.length > 0) {
    sections.push({ key: '', label: ungroupedLabel, conversations: ungrouped });
  }

  return sections;
}
